import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import {
    downloadRolloutReport,
    ReadinessState,
    reevaluateRolloutWatch,
    RolloutEvent,
    RolloutEventKind,
    RolloutProblem,
    RolloutWatchView,
    setMoneyCheck,
    stopRolloutWatch,
    TelemetryProblem,
} from '../../api/rollout';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { useRolloutWatch } from '../../hooks/useRollout';
import { dossierLink } from '../Issues/issueFormat';
import { RolloutCharts } from './RolloutCharts';
import {
    ago,
    buildSummary,
    clockSeconds,
    example,
    objectTiles,
    OWNER_LABEL,
    parseBuilds,
    RangeKey,
    RANGES,
    shortApp,
    span,
    WATCH_STATUS,
} from './rolloutModel';
import {
    Card,
    CheckRow,
    ExactMeters,
    Fresh,
    Kpi,
    ReadingsLive,
    StateIcon,
    WatchWindow,
} from './rolloutParts';

const EVENT_ICON: Record<RolloutEventKind, { icon: string; tone: string }> = {
    Started: { icon: '▶', tone: 'app' },
    Restarted: { icon: '↻', tone: 'app' },
    Stopped: { icon: '■', tone: 'bad' },
    Silent: { icon: '…', tone: 'bad' },
    Deploy: { icon: '⇪', tone: 'deploy' },
    Problem: { icon: '!', tone: 'bad' },
    Telemetry: { icon: '!', tone: 'bad' },
    Light: { icon: '✕', tone: 'bad' },
    LightCleared: { icon: '✓', tone: 'ok' },
};

/** Events whose text says it all: the subject is a code or a key, not an app. */
const TEXT_ONLY: RolloutEventKind[] = [
    'Problem',
    'Telemetry',
    'Light',
    'LightCleared',
];

const AppsCard = ({ view, now }: { view: RolloutWatchView; now: number }) => (
    <Card
        title="Apps"
        aside={
            <Fresh
                at={view.freshness.apps}
                every={view.freshness.appsEvery}
                now={now}
            />
        }
    >
        {view.apps.length === 0 ? (
            <p className="ro-muted">Geen hartslagen</p>
        ) : (
            <table className="ro-table">
                <thead>
                    <tr>
                        <th className="is-state" aria-label="Status" />
                        <th>App</th>
                        <th>Draait</th>
                        <th>Build</th>
                        <th className="is-num">Hartslag</th>
                    </tr>
                </thead>
                <tbody>
                    {view.apps.map((a) => (
                        <tr
                            key={a.app}
                            className={
                                a.running === 0 ? 'is-attention' : undefined
                            }
                        >
                            <td className="is-state">
                                <StateIcon
                                    state={a.running > 0 ? 'Ok' : 'Bad'}
                                />
                            </td>
                            <td className="ro-mono">
                                {a.app}
                                {a.running > 1 && (
                                    <span className="ro-muted">
                                        {' '}
                                        ×{a.running}
                                    </span>
                                )}
                            </td>
                            <td
                                title={
                                    a.startedAt
                                        ? `gestart ${clockSeconds(a.startedAt)}`
                                        : undefined
                                }
                            >
                                {a.running === 0 ? (
                                    <span className="ro-error">gestopt</span>
                                ) : a.startedAt ? (
                                    span(now - Date.parse(a.startedAt))
                                ) : (
                                    '–'
                                )}
                            </td>
                            <td className="ro-mono">{a.build ?? '–'}</td>
                            <td className="is-num">
                                {a.lastBeat
                                    ? `${span(now - Date.parse(a.lastBeat))}`
                                    : '–'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        )}
    </Card>
);

const EventsCard = ({ view, now }: { view: RolloutWatchView; now: number }) => {
    const [all, setAll] = useState(false);
    const deployed = Date.parse(view.deployedAt);
    const recent = view.events.filter(
        (e) => Date.parse(e.at) >= deployed - 3600 * 1000
    );
    const shown = all ? view.events : recent.slice(0, 12);
    const hidden = view.events.length - shown.length;

    return (
        <Card
            title="Gebeurtenissen"
            aside={view.events.length > 0 ? `${view.events.length}` : undefined}
        >
            {shown.length === 0 ? (
                <p className="ro-muted">Nog niets gebeurd</p>
            ) : (
                <ol className="ro-events">
                    {shown.map((e: RolloutEvent) => (
                        <li
                            key={`${e.at}-${e.kind}-${e.subject}`}
                            className={`ro-event is-${EVENT_ICON[e.kind].tone}`}
                        >
                            <span className="ro-event__icon" aria-hidden>
                                {EVENT_ICON[e.kind].icon}
                            </span>
                            <span className="ro-event__text">
                                {TEXT_ONLY.includes(e.kind) || !e.subject ? (
                                    e.text
                                ) : (
                                    <>
                                        <span className="ro-mono">
                                            {e.subject}
                                        </span>{' '}
                                        {e.text}
                                    </>
                                )}
                            </span>
                            <span
                                className="ro-event__when ro-muted"
                                title={new Date(e.at).toLocaleString('nl-NL')}
                            >
                                {span(now - Date.parse(e.at))}
                            </span>
                        </li>
                    ))}
                </ol>
            )}
            {(hidden > 0 || all) && (
                <button
                    type="button"
                    className="ro-more"
                    onClick={() => setAll(!all)}
                >
                    {all
                        ? 'Alleen de laatste'
                        : `Alle ${view.events.length} tonen`}
                </button>
            )}
        </Card>
    );
};

const TimelineCard = ({
    view,
    now,
}: {
    view: RolloutWatchView;
    now: number;
}) => {
    const [range, setRange] = useState<RangeKey>(
        view.status === 'watching' ? 'hour' : 'window'
    );
    const chosen = RANGES.find((r) => r.key === range) ?? RANGES[0];

    return (
        <Card
            title="Verloop"
            aside={
                <span className="ro-card__tools">
                    <Fresh
                        at={view.freshness.objects}
                        every={view.freshness.objectsEvery}
                        now={now}
                    />
                    <span
                        className="ro-segments ro-segments--light"
                        role="group"
                        aria-label="Bereik"
                    >
                        {RANGES.map((r) => (
                            <button
                                key={r.key}
                                type="button"
                                className={range === r.key ? 'is-active' : ''}
                                onClick={() => setRange(r.key)}
                            >
                                {r.label}
                            </button>
                        ))}
                    </span>
                </span>
            }
            footer={<span className="ro-muted">{chosen.per}</span>}
        >
            <RolloutCharts view={view} now={now} range={range} />
        </Card>
    );
};

const worst = (states: ReadinessState[]): ReadinessState =>
    states.includes('Bad')
        ? 'Bad'
        : states.includes('Warn')
        ? 'Warn'
        : states.includes('Unknown')
        ? 'Unknown'
        : 'Ok';

const DossierLinks = ({
    view,
    keys,
}: {
    view: RolloutWatchView;
    keys: string[];
}) => (
    <span className="ro-table__links">
        {keys.slice(0, 6).map((key) => {
            const { type, id } = example(key);

            return (
                <Link
                    key={key}
                    to={dossierLink(view.customer, view.environment, id)}
                    title={`${type}: dossier openen`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {id}
                </Link>
            );
        })}
        {keys.length > 6 && (
            <span className="ro-muted">+{keys.length - 6}</span>
        )}
    </span>
);

/** Rows after the deploy always; the ones only from before behind a click. */
function useOlder<T extends { after: number }>(rows: T[]) {
    const [open, setOpen] = useState(false);
    const current = rows.filter((r) => r.after > 0);
    const older = rows.filter((r) => r.after === 0);

    return {
        shown: open ? [...current, ...older] : current,
        current,
        older,
        toggle: older.length > 0 && (
            <button
                type="button"
                className="ro-more"
                onClick={() => setOpen(!open)}
            >
                {open
                    ? 'Alleen sinds de deploy'
                    : `${older.length} alleen vóór de deploy tonen`}
            </button>
        ),
    };
}

const ProblemsCard = ({
    view,
    now,
}: {
    view: RolloutWatchView;
    now: number;
}) => {
    const { shown, current, toggle } = useOlder<RolloutProblem>(view.problems);
    const fresh = view.problems.filter((p) => p.isNew).length;

    return (
        <Card
            title="Probleemgroepen"
            aside={
                <span className="ro-card__tools">
                    {fresh > 0 && (
                        <span className="ro-pill ro-pill--new">
                            {fresh} nieuw
                        </span>
                    )}
                    <Fresh
                        at={view.freshness.tables}
                        every={view.freshness.tablesEvery}
                        now={now}
                    />
                </span>
            }
        >
            {current.length === 0 && (
                <p className="ro-muted">
                    Geen mislukte pogingen sinds de deploy.
                </p>
            )}
            {shown.length > 0 && (
                <div className="ro-scroll">
                    <table className="ro-table">
                        <thead>
                            <tr>
                                <th>Probleem</th>
                                <th>Wie</th>
                                <th className="is-num">Vóór</th>
                                <th className="is-num">Na</th>
                                <th className="is-num">Objecten</th>
                                <th className="is-num">Hersteld</th>
                                <th>Laatst</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map((p) => (
                                <tr
                                    key={p.code}
                                    className={
                                        p.isNew ? 'is-attention' : undefined
                                    }
                                >
                                    <td className="is-wrap">
                                        <span className="ro-table__title">
                                            {p.isNew && (
                                                <span className="ro-pill ro-pill--new">
                                                    nieuw
                                                </span>
                                            )}
                                            {!p.isNew &&
                                                (p.backlog ?? 0) > 0 && (
                                                    <span
                                                        className="ro-pill"
                                                        title="Al mislukt vóór de deploy"
                                                    >
                                                        oud
                                                    </span>
                                                )}{' '}
                                            {p.title}
                                        </span>
                                        <span className="ro-table__code">
                                            {p.code}
                                        </span>
                                        <DossierLinks
                                            view={view}
                                            keys={p.examples}
                                        />
                                        {p.waitingOn && p.waitingOn.length > 0 && (
                                            <span
                                                className={`ro-table__waits${
                                                    (p.stuck ?? 0) > 0
                                                        ? ' is-stuck'
                                                        : ''
                                                }`}
                                            >
                                                Wacht op{' '}
                                                {p.waitingOn.join(' · ')}
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {p.owner
                                            ? OWNER_LABEL[p.owner] ?? p.owner
                                            : 'Onbekend'}
                                    </td>
                                    <td className="is-num">{p.before}</td>
                                    <td className="is-num">{p.after}</td>
                                    <td className="is-num">{p.objects}</td>
                                    <td className="is-num">{p.healed}</td>
                                    <td>{ago(p.lastAt, now)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {toggle}
        </Card>
    );
};

const TelemetryCard = ({
    view,
    now,
}: {
    view: RolloutWatchView;
    now: number;
}) => {
    const { shown, current, toggle } = useOlder<TelemetryProblem>(
        view.telemetry
    );
    const upTo = view.freshness.telemetryUpTo;

    if (view.telemetryAvailable === false) {
        return (
            <Card title="App Insights">
                <p
                    className="ro-muted"
                    title={view.telemetryUnavailableDetail ?? undefined}
                >
                    {view.telemetryUnavailable ?? 'Telemetrie niet leesbaar'}
                </p>
            </Card>
        );
    }

    return (
        <Card
            title="App Insights"
            aside={
                <Fresh
                    at={view.freshness.telemetry}
                    every={view.freshness.telemetryEvery}
                    now={now}
                    label="gevraagd"
                />
            }
            footer={
                view.telemetryAvailable != null && (
                    <span className="ro-muted">
                        {upTo ? (
                            <span
                                title={`Nieuwste telemetrie van ${clockSeconds(
                                    upTo
                                )}; App Insights loopt 1 tot 3 minuten achter`}
                            >
                                Nieuwste telemetrie:{' '}
                                {span(now - Date.parse(upTo))} oud
                            </span>
                        ) : (
                            'Geen telemetrie in het afgelopen half uur'
                        )}
                    </span>
                )
            }
        >
            {view.telemetryAvailable != null && current.length === 0 && (
                <p className="ro-muted">Geen fouten sinds de deploy</p>
            )}
            {current.length > 0 && current.every((t) => t.expectedReason) && (
                <p className="ro-muted">
                    Geen onverwachte fouten sinds de deploy
                </p>
            )}
            {shown.length > 0 && (
                <div className="ro-scroll">
                    <table className="ro-table">
                        <thead>
                            <tr>
                                <th>Fout</th>
                                <th>App</th>
                                <th className="is-num">Vóór</th>
                                <th className="is-num">Na</th>
                                <th>Laatst</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map((t) => (
                                <tr
                                    key={t.code}
                                    className={
                                        t.isNew
                                            ? 'is-attention'
                                            : t.expectedReason
                                            ? 'is-expected'
                                            : undefined
                                    }
                                >
                                    <td className="is-wrap">
                                        <span className="ro-table__title">
                                            {t.isNew && (
                                                <span className="ro-pill ro-pill--new">
                                                    nieuw
                                                </span>
                                            )}
                                            {t.expectedReason && (
                                                <span
                                                    className="ro-pill ro-pill--ok"
                                                    title={t.expectedReason}
                                                >
                                                    verwacht
                                                </span>
                                            )}{' '}
                                            <span
                                                className="ro-clamp"
                                                title={
                                                    t.sample
                                                        ? `${t.sample}\n\nPatroon: ${t.pattern}`
                                                        : t.pattern
                                                }
                                            >
                                                {t.sample ?? t.pattern}
                                            </span>
                                        </span>
                                        <span className="ro-table__code">
                                            {t.kind === 'exception'
                                                ? 'exception'
                                                : 'log'}
                                            {t.operation
                                                ? ` · ${t.operation}`
                                                : ''}
                                            {t.expectedReason
                                                ? ` · ${t.expectedReason.toLowerCase()}`
                                                : t.expectedAfter > 0
                                                ? ` · ${t.expectedAfter} van ${t.after} verwacht`
                                                : ''}
                                        </span>
                                    </td>
                                    <td className="ro-mono">
                                        {t.role ? shortApp(t.role) : '–'}
                                    </td>
                                    <td className="is-num">{t.before}</td>
                                    <td className="is-num">{t.after}</td>
                                    <td>{ago(t.last, now)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {toggle}
        </Card>
    );
};

const RollbackCard = ({ view }: { view: RolloutWatchView }) => {
    const state = worst(view.lights.map((l) => l.state));
    const bad = state === 'Bad';

    return (
        <Card
            title="Terugzetten?"
            tone={bad ? 'bad' : state === 'Warn' ? 'warn' : 'ok'}
            aside={
                bad ? (
                    <span className="ro-pill ro-pill--bad">overwegen</span>
                ) : (
                    <span className="ro-pill ro-pill--ok">geen reden</span>
                )
            }
            footer={
                view.rollback.rollbackRun ? (
                    <>
                        Terugzetpunt:{' '}
                        <strong className="ro-mono">
                            {view.rollback.rollbackRun}
                        </strong>
                        {view.rollback.afterRollback && (
                            <div
                                className="ro-muted"
                                title={
                                    view.rollback.afterRollbackDetail ??
                                    undefined
                                }
                            >
                                {view.rollback.afterRollback}
                            </div>
                        )}
                    </>
                ) : (
                    <span className="ro-muted">
                        Geen terugzetpunt genoteerd
                    </span>
                )
            }
        >
            <ul className="ro-checks">
                {view.lights.map((l) => (
                    <CheckRow
                        key={l.key}
                        state={l.state}
                        label={l.label}
                        detail={l.detail}
                    />
                ))}
            </ul>
        </Card>
    );
};

const ChecksCard = ({ view, now }: { view: RolloutWatchView; now: number }) => {
    const axios = useAxiosSwagger();
    const canaryState =
        view.canaries.length === 0
            ? 'Unknown'
            : worst(view.canaries.map((c) => c.state));
    const w = view.webhooks;
    const webhookState: ReadinessState =
        w.error || !w.checkedAt
            ? 'Unknown'
            : w.failed > 0
            ? 'Bad'
            : w.rescheduled + w.stuck > 0
            ? 'Warn'
            : 'Ok';
    const s = view.scanner;
    const scannerState: ReadinessState = !s?.sweptAt
        ? 'Unknown'
        : s.missing.length + s.reclassified.length > 0
        ? 'Warn'
        : 'Ok';
    const m = view.money;
    const moneyState: ReadinessState =
        !m.enabled || !m.checkedAt
            ? 'Unknown'
            : m.differences.length > 0
            ? 'Bad'
            : 'Ok';
    const reevaluation = view.reevaluationState;

    return (
        <Card title="Controles">
            <ul className="ro-checks">
                <CheckRow
                    state={canaryState}
                    label="Kanaries"
                    detail={
                        view.canaries.length === 0
                            ? 'Nog niets gekoppeld'
                            : `${
                                  view.canaries.filter((c) => c.state === 'Ok')
                                      .length
                              } van ${view.canaries.length} in Exact gevonden`
                    }
                >
                    {view.canaries.length > 0 && (
                        <table className="ro-table">
                            <tbody>
                                {view.canaries.map((c) => (
                                    <tr key={c.objectType}>
                                        <td className="is-state">
                                            <StateIcon state={c.state} />
                                        </td>
                                        <td>
                                            {c.objectType}
                                            <span className="ro-table__links">
                                                <Link
                                                    to={dossierLink(
                                                        view.customer,
                                                        view.environment,
                                                        c.relationId
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {c.relationId}
                                                </Link>
                                            </span>
                                        </td>
                                        <td
                                            className="ro-muted"
                                            title={c.reason ?? undefined}
                                        >
                                            {c.detail}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CheckRow>
                <CheckRow
                    state={webhookState}
                    label="Webhooks van Chargebee"
                    detail={
                        <>
                            {w.error ? (
                                <span title={w.error}>Niet gelezen</span>
                            ) : !w.checkedAt ? (
                                'Nog niet gelezen'
                            ) : w.failed + w.rescheduled + w.stuck === 0 ? (
                                'Alles afgeleverd'
                            ) : (
                                `${w.failed} mislukt, ${w.rescheduled} wacht op nieuwe poging, ${w.stuck} hangt`
                            )}{' '}
                            {w.checkedAt && (
                                <Fresh
                                    at={w.checkedAt}
                                    every={view.freshness.webhooksEvery}
                                    now={now}
                                    label="gevraagd"
                                />
                            )}
                        </>
                    }
                />
                <CheckRow
                    state={scannerState}
                    label="Issue-scanner"
                    detail={
                        !s?.sweptAt
                            ? 'Nog geen scan'
                            : `${s.agree} gelijk, ${s.missing.length} gemist, ${
                                  s.reclassified.length
                              } anders ingedeeld, ${
                                  s.resolvedSinceDeploy
                              } opgelost · scan ${ago(s.sweptAt, now)}`
                    }
                >
                    {s && s.missing.length > 0 && (
                        <DossierLinks view={view} keys={s.missing} />
                    )}
                </CheckRow>
                <CheckRow
                    state={moneyState}
                    label="Geldcontrole"
                    detail={
                        !m.enabled
                            ? 'Uit · per factuur 1 Chargebee- en 1 Exact-vraag'
                            : !m.checkedAt
                            ? 'Aan · eerste ronde volgt'
                            : `${m.checked} vergeleken: ${m.equal} gelijk, ${m.differences.length} verschil, ${m.skipped} overgeslagen`
                    }
                    action={
                        axios && (
                            <label className="ro-switch">
                                <input
                                    type="checkbox"
                                    checked={m.enabled}
                                    onChange={(e) =>
                                        setMoneyCheck(
                                            axios,
                                            view.id,
                                            e.target.checked
                                        )
                                    }
                                />
                                {m.enabled ? 'aan' : 'uit'}
                            </label>
                        )
                    }
                >
                    {m.differences.length > 0 && (
                        <table className="ro-table">
                            <tbody>
                                {m.differences.map((d) => (
                                    <tr key={d.relationId}>
                                        <td>
                                            <Link
                                                to={dossierLink(
                                                    view.customer,
                                                    view.environment,
                                                    d.relationId
                                                )}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {d.relationId}
                                            </Link>
                                        </td>
                                        <td className="is-num">
                                            {d.chargebee ?? '–'}
                                        </td>
                                        <td className="is-num">
                                            {d.exact ?? '–'}
                                        </td>
                                        <td className="ro-muted">{d.reason}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CheckRow>
                <CheckRow
                    state={
                        reevaluation === 'Done'
                            ? 'Ok'
                            : reevaluation?.startsWith('Mislukt')
                            ? 'Bad'
                            : 'Unknown'
                    }
                    label="Herbeoordeling"
                    detail={
                        reevaluation === 'Running'
                            ? 'Proefronde loopt'
                            : reevaluation === 'Done'
                            ? 'Proefronde klaar'
                            : reevaluation ??
                              '1u na de deploy, bij ruimte in Exact'
                    }
                    action={
                        <span className="ro-actions">
                            {axios && reevaluation !== 'Running' && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() =>
                                        reevaluateRolloutWatch(axios, view.id)
                                    }
                                >
                                    Nu
                                </button>
                            )}
                            <Link
                                to="/issues?tab=herbeoordeling"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-link"
                            >
                                Openen
                            </Link>
                        </span>
                    }
                />
            </ul>
        </Card>
    );
};

/** The latest deploy of an environment, watched: the window, the figures, what went wrong, and the checks. */
export const WatchDashboard = ({
    watchId,
    now,
}: {
    watchId: string;
    now: number;
}) => {
    const { view, live: connected } = useRolloutWatch(watchId);
    const axios = useAxiosSwagger();

    if (!view) {
        return (
            <section className="ro-card">
                <div className="ro-empty">
                    <FontAwesomeIcon icon="spinner" spin /> Wacht laden (24 uur
                    terug)
                </div>
            </section>
        );
    }

    const fresh =
        view.problems.filter((p) => p.isNew).length +
        view.telemetry.filter((t) => t.isNew).length;
    const deployed = buildSummary(
        view.apps.length > 0 ? view.apps : parseBuilds(view.buildAfter)
    );
    // A watch that is over or stopped is no longer read: its cards show how old the data is, not a next reading.
    const reading = view.status === 'watching' && now < Date.parse(view.endsAt);

    return (
        <ReadingsLive.Provider value={reading}>
            <section className="ro-card">
                <div className="ro-status">
                    <div className="ro-status__when">
                        <strong>
                            {view.kind === 'monitor' ? 'Monitor' : 'Deploy'}{' '}
                            {ago(view.deployedAt, now)}
                            <span className={`ro-pill ro-pill--${view.status}`}>
                                {WATCH_STATUS[view.status]}
                            </span>
                            {reading && !connected && (
                                <span
                                    className="ro-pill ro-pill--warn"
                                    title="Verbinding met het dashboard weg; opnieuw verbinden"
                                >
                                    niet verbonden
                                </span>
                            )}
                        </strong>
                        <span className="ro-muted">
                            {deployed ||
                                `gestart door ${view.startedBy ?? 'onbekend'}`}
                        </span>
                        {view.error && (
                            <span className="ro-error" title={view.error}>
                                Lezen mislukt
                            </span>
                        )}
                    </div>
                    <WatchWindow
                        deployedAt={view.deployedAt}
                        endsAt={view.endsAt}
                        now={now}
                    />
                    <div className="ro-status__actions">
                        {axios && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() =>
                                    downloadRolloutReport(axios, view)
                                }
                            >
                                Verslag
                            </button>
                        )}
                        {axios && view.status === 'watching' && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => stopRolloutWatch(axios, view.id)}
                            >
                                Stoppen
                            </button>
                        )}
                    </div>
                </div>
            </section>

            <div className="ro-kpis">
                {objectTiles(view.afterObjects, view.beforeObjects).map((t) => (
                    <Kpi
                        key={t.label}
                        label={t.label}
                        value={t.value}
                        sub={t.sub}
                        tone={
                            t.label === 'Mislukt' && t.value > 0
                                ? 'warn'
                                : undefined
                        }
                    />
                ))}
                <Kpi
                    label="Vanzelf hersteld"
                    value={view.healed}
                    sub="mislukt, daarna gelukt"
                    tone={view.healed > 0 ? 'ok' : undefined}
                />
                <Kpi
                    label="Nieuwe soorten fout"
                    value={fresh}
                    sub="alleen na de deploy"
                    tone={fresh > 0 ? 'bad' : undefined}
                />
            </div>

            <div className="ro-grid">
                <div className="ro-grid__col">
                    <TimelineCard view={view} now={now} />
                    <ProblemsCard view={view} now={now} />
                    <TelemetryCard view={view} now={now} />
                </div>
                <div className="ro-grid__col">
                    <RollbackCard view={view} />
                    <AppsCard view={view} now={now} />
                    <EventsCard view={view} now={now} />
                    <Card
                        title="Exact"
                        aside={
                            <Fresh
                                at={view.freshness.tables}
                                every={view.freshness.tablesEvery}
                                now={now}
                            />
                        }
                    >
                        <ExactMeters board={view.exactLimits} now={now} />
                    </Card>
                    <ChecksCard view={view} now={now} />
                </div>
            </div>
        </ReadingsLive.Provider>
    );
};
